import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faUnderline,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";

const FormattingToolbar = ({ onFormat, position, show }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const colors = [
    "#000000",
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",
    "#FF00FF",
    "#00FFFF",
    "#FFA500",
    "#800080",
    "#008000",
    "#800000",
    "#008080",
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showColorPicker]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  const handleColorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveSelection();
    setShowColorPicker(!showColorPicker);
  };

  const handleColorSelect = (color) => {
    setShowColorPicker(false);
    setTimeout(() => {
      restoreSelection();
      onFormat("color", color);
    }, 0);
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y - 70}px`,
        backgroundColor: "#2c3e50",
        borderRadius: "4px",
        padding: "3px",
        display: "flex",
        gap: "2px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        zIndex: 10000,
        pointerEvents: "auto",
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {[
        { icon: faBold, cmd: "bold", title: "Bold" },
        { icon: faItalic, cmd: "italic", title: "Italic" },
        { icon: faUnderline, cmd: "underline", title: "Underline" },
      ].map(({ icon, cmd, title }) => (
        <button
          key={cmd}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onFormat(cmd)}
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            padding: "4px 8px",
            cursor: "pointer",
            borderRadius: "3px",
            transition: "background 0.2s",
            fontSize: "11px",
          }}
          onMouseEnter={(e) => (e.target.style.background = "#34495e")}
          onMouseLeave={(e) => (e.target.style.background = "transparent")}
          title={title}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      ))}

      <div style={{ position: "relative" }} ref={colorPickerRef}>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleColorClick}
          style={{
            background: showColorPicker ? "#34495e" : "transparent",
            border: "none",
            color: "#fff",
            padding: "4px 8px",
            cursor: "pointer",
            borderRadius: "3px",
            transition: "background 0.2s",
            fontSize: "11px",
          }}
          onMouseEnter={(e) => {
            if (!showColorPicker) {
              e.target.style.background = "#34495e";
            }
          }}
          onMouseLeave={(e) => {
            if (!showColorPicker) {
              e.target.style.background = "transparent";
            }
          }}
          title="Text Color"
        >
          <FontAwesomeIcon icon={faPalette} />
        </button>

        {showColorPicker && (
          <div
            style={{
              position: "absolute",
              top: "30px",
              left: "0",
              backgroundColor: "#fff",
              borderRadius: "4px",
              padding: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "4px",
              minWidth: "120px",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {colors.map((color) => (
              <button
                key={color}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleColorSelect(color)}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: color,
                  border: "1px solid #ddd",
                  borderRadius: "3px",
                  cursor: "pointer",
                  padding: 0,
                }}
                title={color}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FormattableInput = ({
  value,
  onChange,
  placeholder,
  className,
  style,
  type = "text",
}) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const inputRef = useRef(null);
  const contentEditableRef = useRef(null);
  const isContentEditable = type === "contentEditable";
  const lastValueRef = useRef(value);
  const isComposingRef = useRef(false);

  // Helper function to get cursor position
  const getCursorPosition = () => {
    const selection = window.getSelection();
    if (
      !selection.rangeCount ||
      !contentEditableRef.current?.contains(selection.anchorNode)
    ) {
      return 0;
    }

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(contentEditableRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  };

  // Helper function to set cursor position
  const setCursorPosition = (position) => {
    if (!contentEditableRef.current) return;

    const textNodes = [];
    const getTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        textNodes.push(node);
      } else {
        for (let child of node.childNodes) {
          getTextNodes(child);
        }
      }
    };
    getTextNodes(contentEditableRef.current);

    let charCount = 0;
    for (let node of textNodes) {
      const nextCharCount = charCount + node.length;
      if (position <= nextCharCount) {
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(node, Math.min(position - charCount, node.length));
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      charCount = nextCharCount;
    }

    // If position is beyond text, set to end
    if (textNodes.length > 0) {
      const lastNode = textNodes[textNodes.length - 1];
      const range = document.createRange();
      const sel = window.getSelection();
      range.setStart(lastNode, lastNode.length);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // Initial setup only
  useLayoutEffect(() => {
    if (isContentEditable && contentEditableRef.current) {
      contentEditableRef.current.innerHTML = value || "";
      lastValueRef.current = value;
    }
  }, [isContentEditable]);

  // Update content when value changes externally (not from user typing)
  useEffect(() => {
    if (
      isContentEditable &&
      contentEditableRef.current &&
      !isComposingRef.current
    ) {
      const currentHtml = contentEditableRef.current.innerHTML;
      const newValue = value || "";

      // Only update if the content actually changed from external source
      if (lastValueRef.current !== newValue && currentHtml !== newValue) {
        const cursorPosition = getCursorPosition();
        contentEditableRef.current.innerHTML = newValue;
        lastValueRef.current = newValue;

        // Restore cursor position after a tick
        requestAnimationFrame(() => {
          setCursorPosition(cursorPosition);
        });
      }
    }
  }, [value, isContentEditable]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isContentEditable && contentEditableRef.current) {
        const selection = window.getSelection();
        if (selection.toString().length > 0) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          setToolbarPosition({
            x: rect.left + rect.width / 2 - 75,
            y: rect.top + window.scrollY - 10,
          });
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
        }
      } else if (inputRef.current) {
        const input = inputRef.current;
        const start = input.selectionStart;
        const end = input.selectionEnd;

        if (start !== end && document.activeElement === input) {
          const rect = input.getBoundingClientRect();
          setToolbarPosition({
            x: rect.left + rect.width / 2 - 75,
            y: rect.top + window.scrollY - 10,
          });
          setSelection({ start, end });
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
        }
      }
    };

    const handleClickOutside = (e) => {
      if (
        !(
          inputRef.current?.contains(e.target) ||
          contentEditableRef.current?.contains(e.target)
        )
      ) {
        setShowToolbar(false);
      }
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isContentEditable]);

  const wrapSelectionWithSpan = (styleObj) => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const span = document.createElement("span");

    Object.entries(styleObj).forEach(([key, value]) => {
      span.style[key] = value;
    });

    range.surroundContents(span);

    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStartAfter(span);
    newRange.collapse(true);
    sel.addRange(newRange);
  };

  const handleFormat = (format, colorValue = null) => {
    if (isContentEditable) {
      if (format === "color") {
        wrapSelectionWithSpan({ color: colorValue });
      } else {
        document.execCommand(format, false, null);
      }

      if (onChange && contentEditableRef.current) {
        lastValueRef.current = contentEditableRef.current.innerHTML;
        onChange({ target: { value: contentEditableRef.current.innerHTML } });
      }

      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          setToolbarPosition({
            x: rect.left + rect.width / 2 - 75,
            y: rect.top + window.scrollY - 10,
          });
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
        }
      }, 10);
    } else {
      const input = inputRef.current;
      const { start, end } = selection;
      const selectedText = value.substring(start, end);

      let formattedText;
      if (format === "color") {
        formattedText = `<span style="color: ${colorValue}">${selectedText}</span>`;
      } else {
        const tagMap = {
          bold: "b",
          italic: "i",
          underline: "u",
        };
        const tag = tagMap[format];
        formattedText = `<${tag}>${selectedText}</${tag}>`;
      }

      const newValue =
        value.substring(0, start) + formattedText + value.substring(end);
      onChange?.({ target: { value: newValue } });
      setShowToolbar(false);
    }
  };

  const handleContentEditableChange = (e) => {
    if (isComposingRef.current) return;

    const element = e.currentTarget;
    let newValue = element.innerHTML;

    // Check if content is effectively empty (only contains <br> or whitespace)
    const textContent = element.textContent.trim();
    if (!textContent || newValue === "<br>" || newValue === "<br/>") {
      newValue = "";
      element.innerHTML = "";
    }

    lastValueRef.current = newValue;
    onChange?.({ target: { value: newValue } });
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (e) => {
    isComposingRef.current = false;
    handleContentEditableChange(e);
  };

  if (isContentEditable) {
    return (
      <>
        <style>
          {`
            [contenteditable][data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #999;
              pointer-events: none;
              font-style: italic;
            }
            [contenteditable][data-placeholder]:not(:focus):empty:before {
              content: attr(data-placeholder);
              color: #999;
              pointer-events: none;
              font-style: italic;
            }
            [contenteditable]:focus {
              outline: none;
              border-color: #4f46e5;
            }
          `}
        </style>

<div
  ref={contentEditableRef}
  contentEditable
  suppressContentEditableWarning
  onInput={handleContentEditableChange}
  onCompositionStart={handleCompositionStart}
  onCompositionEnd={handleCompositionEnd}
  className={className}
  style={{
    ...style,
    width: "100%",           // ← Force full width
    minHeight: "20px",       // Better for long text like consent
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    outline: "none",
    fontSize: "14px",
    lineHeight: "1.5",
    resize: "vertical",      // Optional: allow vertical resize
    boxSizing: "border-box",
  }}
  data-placeholder={placeholder}
/>        <FormattingToolbar
          show={showToolbar}
          position={toolbarPosition}
          onFormat={handleFormat}
        />
      </>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        style={{
          ...style,
          maxWidth: "600px",
          fontSize: "13px",
        }}
      />
      <FormattingToolbar
        show={showToolbar}
        position={toolbarPosition}
        onFormat={handleFormat}
      />
    </>
  );
};

export { FormattableInput, FormattingToolbar };
